import { useEffect, useState } from "react";
import { api } from "../api";
import { LeadEditModal } from "./LeadEditModal";
import { LeadDuplicates } from "./LeadDuplicates";
import { LeadEmail } from "./LeadEmail";
import { LeadTasks } from "./LeadTasks";
import { TransferModal } from "./TransferModal";
import { LeadTimeline } from "./LeadTimeline";
import type { CatalogItem, CrmCatalog, LeadView } from "../types";

export const TEMPERATURE: Record<
  LeadView["temperature"],
  { label: string; cls: string }
> = {
  hot: { label: "🔥 горячий", cls: "red" },
  warm: { label: "🙂 тёплый", cls: "amber" },
  cold: { label: "❄️ холодный", cls: "blue" },
};

export function labelFor(list: CatalogItem[], key: string): string {
  return list.find((i) => i.key === key)?.label ?? key;
}

/** Карточка клиента, собранная из разговора на очной встрече. */
export function LeadCard({
  lead,
  onChanged,
  onDelete,
}: {
  lead: LeadView;
  onChanged: (lead: LeadView) => void;
  /** Есть только у администратора */
  onDelete?: () => void;
}) {
  const [catalog, setCatalog] = useState<CrmCatalog | null>(null);
  const [editing, setEditing] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [notice, setNotice] = useState("");
  const [archiving, setArchiving] = useState(false);

  const toggleArchive = async () => {
    setArchiving(true);
    try {
      const updated = await api.setLeadArchived(lead.meeting_id, !lead.archived_at);
      setNotice(
        updated.archived_at
          ? "Карточка в архиве: из списка, воронки и отчёта она пропала. Найти её можно фильтром «Архив» на странице «Лиды»."
          : "Карточка возвращена из архива.",
      );
      onChanged(updated);
    } catch (e) {
      setNotice(e instanceof Error ? e.message : String(e));
    } finally {
      setArchiving(false);
    }
  };

  useEffect(() => {
    api.leadCatalog().then(setCatalog).catch(() => setCatalog(null));
  }, []);

  const title = [lead.company, lead.contact_name].filter(Boolean).join(" — ");
  const temp = TEMPERATURE[lead.temperature];
  const locked = new Set(lead.locked_fields);

  return (
    <div className="card-body">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h2 style={{ margin: 0 }}>{title || lead.meeting_topic}</h2>
        <div className="row">
          {lead.merged_into === null && (
            <button className="sm" onClick={() => setTransferring(true)}>
              Передать
            </button>
          )}
          <button className="sm" onClick={() => setEditing(true)}>
            Править карточку
          </button>
          <button
            className="sm"
            disabled={archiving}
            title={
              lead.archived_at
                ? "Вернуть карточку в список, воронку и отчёт"
                : "Неактуальный контакт: пропадёт из списка, воронки, отчёта и напоминаний, но не удалится"
            }
            onClick={toggleArchive}
          >
            {lead.archived_at ? "Вернуть из архива" : "В архив"}
          </button>
          {onDelete && (
            <button className="sm danger" onClick={onDelete}>
              Удалить
            </button>
          )}
        </div>
      </div>

      {notice && <div className="banner green-banner" style={{ marginTop: 12 }}>{notice}</div>}

      <div className="row" style={{ margin: "14px 0 18px" }}>
        {lead.archived_at && (
          <span
            className="badge"
            title={`В архиве с ${new Date(lead.archived_at).toLocaleDateString("ru-RU")}`}
          >
            🗄 в архиве
          </span>
        )}
        <span className={`badge ${temp.cls}`}>{temp.label}</span>
        {catalog && lead.client_type && (
          <span className="badge">{labelFor(catalog.client_types, lead.client_type)}</span>
        )}
        {lead.category && <span className="badge">Категория {lead.category}</span>}
        {lead.city && <span className="badge">{lead.city}</span>}
        {catalog &&
          lead.interests.map((i) => (
            <span key={i} className="badge blue">
              {labelFor(catalog.interests, i)}
            </span>
          ))}
        {catalog && (
          <span className="badge lime">{labelFor(catalog.statuses, lead.status)}</span>
        )}
        {lead.stale && (
          <span
            className="badge red"
            title="Ни звонков, ни писем, ни заметок дольше порога — клиент остывает"
          >
            тишина {lead.silent_days} дн.
          </span>
        )}
        {lead.duplicate_count > 0 && (
          <span
            className="badge amber"
            title="Эта компания есть в базе ещё раз — объединить можно ниже, в разделе «Дубли»"
          >
            дубль ×{lead.duplicate_count + 1}
          </span>
        )}
      </div>

      {lead.contact_role && <div className="muted">{lead.contact_role}</div>}
      {lead.contacts && <div className="mono">{lead.contacts}</div>}

      {(lead.follow_up_at || lead.event_name || lead.owner_name || lead.recorded_by_name) && (
        <div className="row small muted" style={{ marginTop: 10 }}>
          {lead.follow_up_at && (
            <span>
              ⏰ вернуться{" "}
              {new Date(lead.follow_up_at).toLocaleDateString("ru-RU", {
                day: "numeric",
                month: "long",
              })}
            </span>
          )}
          {lead.event_name && <span>· {lead.event_name}</span>}
          {lead.owner_name && <span>· ведёт: {lead.owner_name}</span>}
          {lead.recorded_by_name && lead.recorded_by_name !== lead.owner_name && (
            <span>· переговоры вёл(а): {lead.recorded_by_name}</span>
          )}
        </div>
      )}

      {lead.summary && (
        <p className="summary-block" style={{ marginTop: 18 }}>
          {lead.summary}
        </p>
      )}

      {lead.business && (
        <>
          <h3>Чем занимаются</h3>
          <p className="summary-block">{lead.business}</p>
        </>
      )}

      {lead.needs && (
        <>
          <h3>Что нужно {locked.has("needs") && <Manual />}</h3>
          <p className="summary-block">{lead.needs}</p>
        </>
      )}

      {(lead.budget || lead.timeline) && (
        <div className="grid-2">
          {lead.budget && (
            <div>
              <label>Бюджет</label>
              <div>{lead.budget}</div>
            </div>
          )}
          {lead.timeline && (
            <div>
              <label>Сроки</label>
              <div>{lead.timeline}</div>
            </div>
          )}
        </div>
      )}

      {lead.agreements.length > 0 && (
        <>
          <h3>Договорились</h3>
          <ul className="list-clean">
            {lead.agreements.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </>
      )}

      <LeadTasks meetingId={lead.meeting_id} />

      {lead.notes && (
        <>
          <h3>Заметки менеджера</h3>
          <p className="summary-block">{lead.notes}</p>
        </>
      )}

      <LeadDuplicates lead={lead} onChanged={onChanged} />

      <LeadEmail lead={lead} onChanged={onChanged} />

      <LeadTimeline meetingId={lead.meeting_id} catalog={catalog} />

      <div className="muted small" style={{ marginTop: 18 }}>
        {lead.model.startsWith("import:")
          ? "Загружено из таблицы"
          : `Собрано моделью ${lead.model}`}{" "}
        ·{" "}
        {new Date(lead.created_at).toLocaleString("ru-RU")}
        {locked.size > 0 && ` · полей поправлено вручную: ${locked.size}`}
      </div>

      {transferring && (
        <TransferModal
          lead={lead}
          onClose={() => setTransferring(false)}
          onDone={(updated, toName) => {
            setTransferring(false);
            setNotice(`Клиент передан: ${toName}. Ему ушло сообщение в телеграм.`);
            onChanged(updated);
          }}
        />
      )}

      {editing && (
        <LeadEditModal
          lead={lead}
          catalog={catalog}
          onClose={() => setEditing(false)}
          onSaved={(updated) => {
            setEditing(false);
            onChanged(updated);
          }}
        />
      )}
    </div>
  );
}

function Manual() {
  return (
    <span className="badge" title="Поле поправлено вручную — разбор его не перезапишет">
      вручную
    </span>
  );
}
